/**
 * Custom hook for file upload functionality
 * Manages file upload state, validation, and retry logic
 */

import type { UploadedFile } from "@/stores/ChatStore";

import { useState, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";

import {
  prepareFilesForUpload,
  replaceTemporaryFiles,
  removeFileByName,
  hasFilesInProgress,
  calculateUploadProgress,
} from "@/utils/chat/fileOperations";
import {
  createValidationError,
  shouldBlockUpload,
  getFirstFailedFile,
  type ValidationResult,
} from "@/utils/chat/validationHelpers";
import { logger } from "@/telemetry";

/**
 * Configuration options for the useFileUpload hook
 */
export interface UseFileUploadProps {
  /** Async function to handle the actual file upload to the server */
  onUpload?: (files: File[]) => Promise<UploadedFile[]>;
  /** Optional validation function to check files before upload */
  onValidate?: (files: File[]) => ValidationResult;
  /** Maximum number of files allowed (default: 10) */
  maxFiles?: number;
  /** Accepted file types (not currently used) */
  accept?: string;
}

/**
 * Return type for the useFileUpload hook
 */
export interface UseFileUploadReturn {
  /** Array of currently uploaded/uploading files */
  files: UploadedFile[];
  /** Current upload progress (0-100) */
  uploadProgress: number;
  /** Whether files are currently being uploaded */
  isUploading: boolean;
  /** Array of files that failed to upload */
  failedFiles: File[];
  /** Reference to the file input element */
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  /** Current error state, null if no error */
  error: Error | null;

  // Actions
  /** Handler for file input change events */
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  /** Handler for drag-and-drop file events */
  handleDrop: (acceptedFiles: File[]) => Promise<void>;
  /** Remove a file by its name */
  removeFile: (fileName: string) => void;
  /** Retry uploading all failed files */
  retryFailedUploads: () => Promise<void>;
  /** Clear all files and reset state */
  clearFiles: () => void;

  // State setters for external control
  /** Manually set the files array (for external state management) */
  setFiles: (files: UploadedFile[]) => void;
  /** Manually set upload progress (for external progress tracking) */
  setUploadProgress: (progress: number) => void;
}

/**
 * Custom hook for managing file uploads with retry logic and progress tracking
 *
 * @param props - Configuration options for the hook
 * @returns Object containing file state, upload handlers, and utility functions
 *
 * @example
 * ```tsx
 * const {
 *   files,
 *   isUploading,
 *   failedFiles,
 *   handleDrop,
 *   retryFailedUploads
 * } = useFileUpload({
 *   onUpload: async (files) => {
 *     // Upload logic here
 *     return uploadedFiles;
 *   },
 *   maxFiles: 5
 * });
 * ```
 */
export const useFileUpload = ({
  onUpload,
  onValidate,
  maxFiles = 10,
  accept: _accept,
}: UseFileUploadProps = {}): UseFileUploadReturn => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [failedFiles, setFailedFiles] = useState<File[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback(
    async (fileList: File[]) => {
      if (!onUpload) {
        logger.warn("No upload handler provided");

        return;
      }

      // Validation
      if (onValidate) {
        const validationResult = onValidate(fileList);

        if (shouldBlockUpload(validationResult)) {
          const validationError = createValidationError(validationResult.errors);
          const firstFailed = getFirstFailedFile(validationResult.errors);

          if (firstFailed) {
            setFailedFiles([firstFailed]);
          }
          setError(validationError);

          return;
        }
      }

      // Check max files
      if (files.length + fileList.length > maxFiles) {
        setError(new Error(t("maxFilesExceeded", `Maximum ${maxFiles} files allowed`)));

        return;
      }

      // Check for duplicate file names
      const existingFileNames = new Set(files.map((f) => f.name.toLowerCase()));
      const duplicateFiles = fileList.filter((file) =>
        existingFileNames.has(file.name.toLowerCase()),
      );

      if (duplicateFiles.length > 0) {
        const duplicateNames = duplicateFiles.map((f) => f.name).join(", ");

        setFailedFiles(duplicateFiles);
        setError(new Error(`DUPLICATE_FILE: ${duplicateNames}`));

        return;
      }

      setIsUploading(true);
      setUploadProgress(0);

      // Add temporary upload markers
      const tempFiles = prepareFilesForUpload(fileList, "uploading");

      setFiles((prev) => [...prev, ...tempFiles]);
      setError(null);

      try {
        // Simulate progress
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => calculateUploadProgress(prev, 10, 90));
        }, 200);

        // After initial progress, update status to Processing
        setTimeout(() => {
          setFiles((prev) =>
            prev.map((f) => (f.id?.startsWith("uploading-") ? { ...f, status: "processing" } : f)),
          );
        }, 1000); // Increased from 500ms to show Uploading longer

        // Upload files
        const uploadedFiles = await onUpload(fileList);

        clearInterval(progressInterval);
        setUploadProgress(100);

        // Update status to Completed before replacing
        setFiles((prev) =>
          prev.map((f) => (f.id?.startsWith("uploading-") ? { ...f, status: "completed" } : f)),
        );

        // Wait a moment to show the Completed status
        await new Promise((resolve) => setTimeout(resolve, 1500)); // Increased to show Completed longer

        // Replace temp files with uploaded
        setFiles((prev) => replaceTemporaryFiles(prev, uploadedFiles));
        setFailedFiles([]);
        // Cleanup on success
        setIsUploading(false);
        setUploadProgress(0);
      } catch (error) {
        // Use flushSync to ensure all state updates are committed before throwing
        flushSync(() => {
          setFailedFiles(fileList);
          // Remove temp files on error
          fileList.forEach((file) => {
            setFiles((prev) => removeFileByName(prev, file.name));
          });
          // Reset uploading state
          setIsUploading(false);
          setUploadProgress(0);
          setError(error instanceof Error ? error : new Error("Upload failed"));
        });
        // We don't re-throw here so the UI can handle the error state
        // throw error;
      }
    },
    [maxFiles, onUpload, onValidate, t, files],
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;

      if (!fileList || fileList.length === 0) return;

      const filesArray = Array.from(fileList);

      await processFiles(filesArray);

      // Clear input value to allow re-upload of same files
      if (event.target.value) {
        event.target.value = "";
      }
    },
    [processFiles],
  );

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      await processFiles(acceptedFiles);
    },
    [processFiles],
  );

  const removeFile = useCallback((fileName: string) => {
    setFiles((prev) => removeFileByName(prev, fileName));
  }, []);

  const retryFailedUploads = useCallback(async () => {
    if (failedFiles.length === 0) return;

    const filesToRetry = [...failedFiles];

    setFailedFiles([]);
    setError(null);

    await handleDrop(filesToRetry);
  }, [failedFiles, handleDrop]);

  const clearFiles = useCallback(() => {
    setFiles([]);
    setFailedFiles([]);
    setUploadProgress(0);
    setIsUploading(false);
    setError(null);
  }, []);

  return {
    files,
    uploadProgress,
    isUploading: isUploading || hasFilesInProgress(files),
    failedFiles,
    fileInputRef,
    error,
    handleFileChange,
    handleDrop,
    removeFile,
    retryFailedUploads,
    clearFiles,
    setFiles,
    setUploadProgress,
  };
};
